import React from "react";
import { cn } from "../../../common/u1/cn";
import { Button } from "../../../common/u1/Button";
import { IndustryActivities, AddressCard, AboutCompany } from "./ProfilePrimitives";
import { BusinessDetails } from "./BusinessDetails";
import { DocumentsTab } from "./DocumentsTab";
import { FinancialsStrip } from "./FinancialsTab";
import { PREVIEW_COMPANY, PREVIEW_FINANCE } from "./gatedPreviewData";

/* What a gated tab shows behind the offer — F-104. */

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between gap-4 border-b border-[var(--line)] py-3 last:border-b-0">
    <span className="text-[13px] text-[var(--muted)] font-sans">{label}</span>
    <span className="text-[13.5px] font-semibold text-[var(--ink)] font-sans">{value}</span>
  </div>
);

const Panel = ({ title, children, className = "" }) => (
  <section
    className={cn(
      "rounded-[14px] border border-[var(--line)] bg-white p-5 sm:p-6",
      className
    )}
  >
    <h3 className="m-0 mb-1 text-[15px] font-bold text-[var(--ink)] font-sans">{title}</h3>
    <div>{children}</div>
  </section>
);

/* A bar chart drawn from fixed heights — no data, no library, nothing to
   fetch. Heights are percentages of the plot so the shape survives any size. */
const BARS = [38, 56, 44, 72, 63, 88];

const Chart = ({ label }) => (
  <div>
    <p className="m-0 text-[13px] text-[var(--muted)] font-sans">{label}</p>
    <div className="mt-3 flex h-[92px] items-end gap-2" aria-hidden="true">
      {BARS.map((height, i) => (
        <span
          key={`${label}-${i}`}
          style={{ height: `${height}%` }}
          className="grow rounded-t-[3px] bg-[var(--coral-soft)]"
        />
      ))}
    </div>
  </div>
);

/* Tab 02 — Business Details. The REAL tab, against the placeholder payload:
   it reads everything from the company object and fetches nothing, so the
   locked panel shows the same eight sections the purchased view does. */
const BusinessDetailsPreview = () => <BusinessDetails company={PREVIEW_COMPANY} />;

/* Tab 04 — Documents. Likewise real: documentRows reads three fields off the
   payload. The cards are anchors but sit inside the caller's blur wrapper,
   which is aria-hidden with pointer-events-none, so none is reachable. */
const DocumentsPreview = () => <DocumentsTab company={PREVIEW_COMPANY} />;

/* Tab 03 — Financials. The ONE tab whose real content cannot be mounted: FinancialsTab fetches its series and the finance endpoint answers 403 without access,... */
const FinancialsPreview = () => (
  <div className="flex flex-col gap-[22px]">
    <Panel title="Financials">
      <FinancialsStrip finance={PREVIEW_FINANCE} company={PREVIEW_COMPANY} />
      <div className="mt-5 grid grid-cols-1 gap-6 md:grid-cols-2">
        <Chart label="Revenue — last six reported years" />
        <Chart label="Profit — last six reported years" />
      </div>
    </Panel>
    <Panel title="Reported Figures">
      <Row label="Total Revenue" value="AED 0,000,000" />
      <Row label="Gross Profit" value="AED 0,000,000" />
      <Row label="Net Profit" value="AED 0,000,000" />
      <Row label="Total Assets" value="AED 0,000,000" />
    </Panel>
  </div>
);

/* Tab 01 — General Information. */
const PREVIEW_ACTIVITIES = [
  { id: "a1", label: "Industry Name" },
  { id: "a2", label: "Activity Name" },
  { id: "a3", label: "Activity Name" },
  { id: "a4", label: "Sub Industry Name" },
  { id: "a5", label: "Activity Name" },
];

const PREVIEW_ADDRESS = {
  state: "State Name",
  city: "City Name",
  area: "Area Name",
  zip: "2185223",
  addressLine1: "Building Name, Street Name, Area Name",
  email: "email@businessname.com",
  telephone: "000 000 0000",
  phone: "000 000 0000",
  workingHours: "Sun - Thu, 9:00 AM - 6:00 PM",
};

const PREVIEW_DESCRIPTION =
  "<p>Business Name is a company registered in Country Name, operating across " +
  "its industry with a portfolio of activities and a network of trading " +
  "partners. This section carries the full business description, its history, " +
  "its markets and its stated line of business.</p>";

const GeneralInformationPreview = () => (
  <div className="flex flex-col gap-[22px]">
    <IndustryActivities activities={PREVIEW_ACTIVITIES} />
    <div className="grid grid-cols-1 items-start gap-[22px] lg:grid-cols-2">
      <AddressCard address={PREVIEW_ADDRESS} />
      <AboutCompany description={PREVIEW_DESCRIPTION} />
    </div>
  </div>
);

/* Keyed by the tab number the page already uses: 2 Business Details,
   3 Documents, 4 Financials — the live numbering, which the design shows as
   03 and 04 the other way round (see CompanyProfile). */
export const TAB_PREVIEWS = {
  1: GeneralInformationPreview,
  2: BusinessDetailsPreview,
  3: DocumentsPreview,
  4: FinancialsPreview,
};

export const GatedTabPreview = ({ tab, className = "" }) => {
  const Preview = TAB_PREVIEWS[tab];
  if (!Preview) return null;
  return (
    <div className={className} data-testid={`gated-preview-${tab}`}>
      <Preview />
    </div>
  );
};

/* The way back to the offer once it has been dismissed. */
export const GatedTabPrompt = ({ onGetDetails, className = "" }) => (
  <div className={cn("flex justify-center", className)}>
    <Button variant="coralSolid" onClick={onGetDetails}>
      Get Details
    </Button>
  </div>
);

export default GatedTabPreview;
